/**
 * Smart duration formatter shared between the Touch / Dwell chart axis +
 * tooltips and the Active Jobs panel stat tiles. Picks the unit (minutes /
 * hours / days) based on magnitude so values stay readable across scales
 * (long-running RMAs go into days, mid-shift jobs stay in hours, short
 * setup tasks drop into minutes). #3084
 *
 *   value < 1 min    -> "0m"
 *   < 60 min         -> "Xm"
 *   < 24 hours       -> "Xh"  or  "Xh Ym"
 *   >= 24 hours      -> "Xd"  or  "Xd Yh"
 */
export function formatDurationMinutes(totalMin: number | null | undefined): string {
    if (totalMin == null || !Number.isFinite(totalMin) || totalMin < 0) return '\u2014'
    const total = Math.round(totalMin)
    if (total === 0) return '0m'
    if (total < 60) return `${total}m`
    if (total < 1440) {
        const h = Math.floor(total / 60)
        const m = total % 60
        return m === 0 ? `${h}h` : `${h}h ${m}m`
    }
    const d = Math.floor(total / 1440)
    const h = Math.round((total % 1440) / 60)
    return h === 0 ? `${d}d` : `${d}d ${h}h`
}

/** Hours-input convenience wrapper. */
export function formatDurationHours(totalHours: number | null | undefined): string {
    if (totalHours == null) return '\u2014'
    return formatDurationMinutes(totalHours * 60)
}

/**
 * Hours+minutes variant \u2014 same shape as `formatDurationMinutes` but caps the
 * largest unit at hours (never rolls into days). Use when a long-running
 * total reads better as "32h 15m" than "1d 8h" \u2014 e.g. the operator detail
 * page's Total hrs KPI tile, where the column label is literally "hrs".
 */
export function formatDurationMinutesHM(totalMin: number | null | undefined): string {
    if (totalMin == null || !Number.isFinite(totalMin) || totalMin < 0) return '\u2014'
    const total = Math.round(totalMin)
    if (total === 0) return '0m'
    if (total < 60) return `${total}m`
    const h = Math.floor(total / 60)
    const m = total % 60
    return m === 0 ? `${h}h` : `${h}h ${m}m`
}

/** Hours-input wrapper for `formatDurationMinutesHM`. */
export function formatDurationHoursHM(totalHours: number | null | undefined): string {
    if (totalHours == null) return '\u2014'
    return formatDurationMinutesHM(totalHours * 60)
}

/**
 * Active-jobs variant \u2014 same as `formatDurationMinutes` but renders
 * `<1m` for values strictly between 0 and 1 minute. On panels that
 * only show in-flight work, plain `0m` reads like "no data"; this
 * keeps the just-started case (job clocked in seconds ago) explicit.
 */
export function formatDurationMinutesActive(totalMin: number | null | undefined): string {
    if (totalMin != null && Number.isFinite(totalMin) && totalMin > 0 && totalMin < 1) return '<1m'
    return formatDurationMinutes(totalMin)
}
